import {
  ArrowRightIcon,
  Button,
  Tag,
  Text,
  TrashIcon,
  WarningOutlineIcon,
} from '@mercai/clever'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import {
  calculateDateDifference,
  formatCurrency,
  formatLongDate,
  isSuperUser,
} from '@/helpers'

import { useAuth } from '@/providers'
import { ROUTES_ADMIN } from '@/router'

import {
  SaleOrderPaymentCalculatedStatusEnum,
  SaleOrderStatusEnum,
} from '@/types'

import type {
  TableColumnBuyerProps,
  TableColumnDaysToExpireProps,
  TableColumnDeliveredAtProps,
  TableColumnExpectedDeliveryDateProps,
  TableColumnExpectedPaymentDateProps,
  TableColumnInvoiceStatusProps,
  TableColumnLogisticActionsProps,
  TableColumnLogisticStatusProps,
  TableColumnPaymentExpiresAtProps,
  TableColumnPaymentStatusProps,
  TableColumnReferenceProps,
  TableColumnSalesOrderStatusProps,
  TableColumnTagMoscowProps,
  TableColumnTaxStatusProps,
  TableColumnTotalPriceProps,
} from './table-columns.type'

import { SelectLogisticShipmentStatus } from '@/components'
import styles from './table-columns.module.css'

export const TableReference = ({
  reference,
  saleOrderStatus,
}: TableColumnReferenceProps) => {
  const { t: tEnums } = useTranslation('enums')

  const saleOrderStatusObject: Record<SaleOrderStatusEnum, ReactNode> = {
    cancelled: (
      <Tag model="light" variant="danger">
        {tEnums('sale_order_status_cancelled')}
      </Tag>
    ),
    completed: (
      <Tag model="light" variant="success">
        {tEnums('sale_order_status_completed')}
      </Tag>
    ),
    created: (
      <Tag model="light" variant="warning">
        {tEnums('sale_order_status_created')}
      </Tag>
    ),
    fraudulent: (
      <Tag model="light" variant="danger">
        {tEnums('sale_order_status_fraudulent')}
      </Tag>
    ),
    reviewed: (
      <Tag model="light" variant="primary">
        {tEnums('sale_order_status_reviewed')}
      </Tag>
    ),
    processing: (
      <Tag model="light" variant="primary">
        {tEnums('sale_order_status_processing')}
      </Tag>
    ),
  }

  return (
    <div className={styles.reference}>
      {saleOrderStatusObject?.[saleOrderStatus]}

      <Text>#{reference}</Text>
    </div>
  )
}

export const TableColumnBuyer = ({
  buyerName,
  companyName,
  uuid,
}: TableColumnBuyerProps) => {
  const getInitials = () => {
    const name = companyName || buyerName

    const splittedName = name.split(' ')

    let initials = ''

    if (splittedName?.[0]) {
      initials = `${splittedName[0][0]}`
    }

    if (splittedName?.[splittedName?.length - 1]) {
      initials = `${initials}${splittedName[splittedName?.length - 1][0]}`
    }

    return initials
  }

  return (
    <Link
      target="_blank"
      to={ROUTES_ADMIN.PROFILE.SHOW.fullPath(uuid)}
      className={styles.buyer}
    >
      <Text variant="title-small" className={styles.initials}>
        {getInitials()}
      </Text>
      <div className={styles.content}>
        <Text variant="title-base">{companyName || '-'}</Text>
        {buyerName && <Text variant="text-small">{buyerName || ''}</Text>}
      </div>
    </Link>
  )
}

export const TableTaxStatus = ({
  taxStatus = 'pending',
}: TableColumnTaxStatusProps) => {
  const { t } = useTranslation('enums')

  const object: Record<string, ReactNode> = {
    pending: (
      <Tag variant="warning" model="light">
        {t('profile_tax_status_pending')}
      </Tag>
    ),
    review: (
      <Tag variant="danger" model="light">
        {t('profile_tax_status_review')}
      </Tag>
    ),
    completed: (
      <Tag variant="success" model="light">
        {t('profile_tax_status_completed')}
      </Tag>
    ),
    invalid: (
      <Tag variant="basic" model="light">
        {t('profile_tax_status_invalid')}
      </Tag>
    ),
  }

  return <div>{object[taxStatus]}</div>
}

export const TableTagMoscow = ({
  moscowClassification,
}: TableColumnTagMoscowProps) => {
  const { t } = useTranslation('enums')

  const moscowObject: Record<string, ReactNode> = {
    must: (
      <Tag variant="success" model="light">
        {t('profile_moscow_must')}
      </Tag>
    ),
    should: (
      <Tag variant="primary" model="light">
        {t('profile_moscow_should')}
      </Tag>
    ),
    could: (
      <Tag variant="warning" model="light">
        {t('profile_moscow_could')}
      </Tag>
    ),
    wont: (
      <Tag variant="danger" model="light">
        {t('profile_moscow_wont')}
      </Tag>
    ),
    unknown: (
      <Tag variant="basic" model="light">
        {t('profile_moscow_unknown')}
      </Tag>
    ),
  }

  return moscowObject[moscowClassification || 'unknown']
}

export const TableColumnsPaymentStatus = ({
  calculatedPaymentStatus,
}: TableColumnPaymentStatusProps) => {
  const { t } = useTranslation('enums')

  const object: Record<SaleOrderPaymentCalculatedStatusEnum, ReactNode> = {
    processing: (
      <Tag variant="warning" model="light">
        {t('order_payment_calculated_status_processing')}
      </Tag>
    ),
    paid: (
      <Tag variant="success" model="light">
        {t('order_payment_calculated_status_paid')}
      </Tag>
    ),
    invalid: (
      <Tag variant="basic" model="light">
        {t('order_payment_calculated_status_invalid')}
      </Tag>
    ),
    cancelled: (
      <Tag variant="danger" model="light">
        {t('order_payment_calculated_status_cancelled')}
      </Tag>
    ),
    expired: (
      <Tag variant="danger" model="light">
        {t('order_payment_calculated_status_expired')}
      </Tag>
    ),
    partially_paid: (
      <Tag variant="warning" model="light">
        {t('order_payment_calculated_status_partially_paid')}
      </Tag>
    ),
    pending_credit_payment: (
      <Tag variant="warning" model="light">
        {t('order_payment_calculated_status_pending_credit_payment')}
      </Tag>
    ),
    pending_credit_approval: (
      <Tag variant="warning" model="light">
        {t('order_payment_calculated_status_pending_credit_approval')}
      </Tag>
    ),
  }

  return <div>{object[calculatedPaymentStatus]}</div>
}

export const TableColumnsInvoiceStatus = ({
  invoiceStatus,
}: TableColumnInvoiceStatusProps) => {
  const { t } = useTranslation('enums')

  const object: Record<string, ReactNode> = {
    pending: (
      <Tag variant="warning" model="light">
        {t('order_invoice_status_pending')}
      </Tag>
    ),
    uploaded: (
      <Tag variant="success" model="light">
        {t('order_invoice_status_uploaded')}
      </Tag>
    ),
    cancelled: (
      <Tag variant="danger" model="light">
        {t('order_invoice_status_cancelled')}
      </Tag>
    ),
    processing: (
      <Tag variant="warning" model="light">
        {t('order_invoice_status_processing')}
      </Tag>
    ),
    invoiced: (
      <Tag variant="warning" model="light">
        {t('order_invoice_status_invoiced')}
      </Tag>
    ),
  }

  return <div>{object[invoiceStatus]}</div>
}

export const TableColumnsLogisticStatus = ({
  shipment,
  saleOrderUuid,
  queryKeys,
}: TableColumnLogisticStatusProps) => {
  return (
    <div>
      <SelectLogisticShipmentStatus
        saleOrderUuid={saleOrderUuid}
        shipment={shipment}
        queryKeys={queryKeys}
      />
    </div>
  )
}
export const TableColumnsSalesOrderStatus = ({
  salesOrderStatus,
}: TableColumnSalesOrderStatusProps) => {
  const { t } = useTranslation('enums')

  const object: Record<string, ReactNode> = {
    created: (
      <Tag variant="warning" model="light">
        {t('sale_order_status_created')}
      </Tag>
    ),
    processing: (
      <Tag variant="warning" model="light">
        {t('sale_order_status_processing')}
      </Tag>
    ),
    completed: (
      <Tag variant="success" model="light">
        {t('sale_order_status_completed')}
      </Tag>
    ),
    fraudulent: (
      <Tag variant="danger" model="light">
        {t('sale_order_status_fraudulent')}
      </Tag>
    ),
    cancelled: (
      <Tag variant="danger" model="light">
        {t('sale_order_status_cancelled')}
      </Tag>
    ),
  }

  return <div>{object[salesOrderStatus]}</div>
}

export const TableColumnExpectedDeliveryDate = ({
  expectedDeliveryAt,
  deliveryDate,
}: TableColumnExpectedDeliveryDateProps) => {
  const verifyLate = () => {
    const expectedDate = new Date(expectedDeliveryAt)
    expectedDate.setDate(expectedDate.getDate() + 1)

    let delivery = new Date(deliveryDate)

    if (!deliveryDate) {
      delivery = new Date()
    }

    return expectedDate < delivery
  }

  const isLate = verifyLate()

  return (
    <div className={styles['expected-delivery-date']}>
      <Text className={isLate ? styles['is-late'] : undefined}>
        {formatLongDate(expectedDeliveryAt)}
      </Text>

      {isLate && <WarningOutlineIcon />}
    </div>
  )
}

export const TableColumnDeliveredAt = ({
  deliveredAt,
}: TableColumnDeliveredAtProps) => {
  return (
    <div>
      <Text>{deliveredAt ? formatLongDate(deliveredAt) : '-'}</Text>
    </div>
  )
}

export const TableCreditColumnExpectedPaymentDate = ({
  expectedPaymentDate,
}: TableColumnExpectedPaymentDateProps) => {
  const now = new Date()
  const exPaymentDate = new Date(expectedPaymentDate || '')
  const diff = calculateDateDifference(now, exPaymentDate)
  const isExpired = now > exPaymentDate
  let status

  if (isExpired) {
    status = 'is-late'
  } else if (diff < 3) {
    status = 'is-late'
  } else if (diff < 5) {
    status = 'is-warning'
  }

  return (
    <div className={styles['expected-delivery-date']}>
      <Text className={status ? styles[status] : undefined}>
        {expectedPaymentDate ? formatLongDate(expectedPaymentDate) : '-'}
      </Text>

      {isExpired && <WarningOutlineIcon />}
    </div>
  )
}

export const TableCreditColumnDaysToExpire = ({
  expiresAt,
}: TableColumnDaysToExpireProps) => {
  const { t } = useTranslation('screenOrderCenterPage')
  const now = new Date()
  const expireDate = new Date(expiresAt)
  const diff = calculateDateDifference(now, expireDate)
  const isExpired = now > expireDate
  let status

  if (isExpired) {
    status = 'is-late'
  } else if (diff < 3) {
    status = 'is-warning'
  }

  return (
    <div className={styles['expected-delivery-date']}>
      <Text className={status ? styles[status] : undefined}>
        {isExpired && '-'}
        {diff} {t('days')}
      </Text>

      {isExpired && <WarningOutlineIcon />}
    </div>
  )
}

export const TableColumnPaymentExpireAt = ({
  expiresAt,
}: TableColumnPaymentExpiresAtProps) => {
  const now = new Date()
  const expireDate = new Date(expiresAt || '')
  const diff = calculateDateDifference(now, expireDate)
  const isExpired = now > expireDate
  let status

  if (isExpired) {
    status = 'is-late'
  } else if (diff < 3) {
    status = 'is-warning'
  }

  return (
    <div className={styles['expected-delivery-date']}>
      <Text className={status ? styles[status] : undefined}>
        {expiresAt ? formatLongDate(expiresAt) : '-'}
      </Text>

      {isExpired && <WarningOutlineIcon />}
    </div>
  )
}

export const TableColumnTotalPrice = ({
  saleOrder,
}: TableColumnTotalPriceProps) => {
  const currency = saleOrder?.payments?.[0]?.currency
  const currencyMultiplier = +(currency === 'USD'
    ? 1
    : saleOrder?.payments?.[0]?.dollarExchangeRate || 0)

  return (
    <div>
      {formatCurrency(
        +(saleOrder?.totalOrderPrice || 0) * currencyMultiplier,
        'es-MX',
        currency,
      )}
    </div>
  )
}

export const TableColumnActionsButtons = ({
  onClickView,
  onClickDelete,
}: TableColumnLogisticActionsProps) => {
  const { whoAmI } = useAuth()

  return (
    <div className={styles['actions-buttons']}>
      <Button onClick={onClickView} model="square">
        <ArrowRightIcon />
      </Button>

      {isSuperUser(whoAmI?.email) && (
        <Button variant="danger" model="square" onClick={onClickDelete}>
          <TrashIcon />
        </Button>
      )}
    </div>
  )
}

export const OrderTableColumnCreditOrderStatus = ({
  value,
}: {
  value?: string
}) => {
  const { t } = useTranslation('enums')

  if (!value) {
    return '-'
  }

  const stateObject: Record<string, ReactNode> = {
    pending: (
      <Tag variant="warning" model="light">
        {t('credit_order_status_pending')}
      </Tag>
    ),
    active: (
      <Tag variant="primary" model="light">
        {t('credit_order_status_active')}
      </Tag>
    ),
    denied: (
      <Tag variant="danger" model="light">
        {t('credit_order_status_denied')}
      </Tag>
    ),
    paid: (
      <Tag variant="success" model="light">
        {t('credit_order_status_paid')}
      </Tag>
    ),
    payment_overdue: (
      <Tag variant="danger" model="light">
        {t('credit_order_status_payment_overdue')}
      </Tag>
    ),
    cancelled: (
      <Tag variant="danger" model="light">
        {t('credit_order_status_cancelled')}
      </Tag>
    ),
  }

  return stateObject[value] || <Tag variant="warning">{value}</Tag>
}

export const OrderTableColumnFinance = ({
  cm3Margin,
  grossMargin,
}: {
  cm3Margin?: string
  grossMargin?: string
}) => {
  return (
    <div>
      <Text>{`CM3: ${cm3Margin ? (+cm3Margin * 100).toFixed(2) : 0}% | GM: ${grossMargin ? (+grossMargin * 100).toFixed(2) : 0}%`}</Text>
    </div>
  )
}
